import React, { useState } from 'react';
import { Button } from './ui/button';
import { GripVertical, Play, Plus, ListPlus, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatModeLabel } from '../utils/formatters';

/**
 * Parts Manager - Drag & Drop reorderable parts list
 * Allows manual reordering and adding parts to queue
 */
const PartsManager = ({ 
  parts = [], 
  currentPartId = null,
  onSelectPart,
  onAddPartToQueue,
  onReorderParts,
  onCreatePart,
  className = ''
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const handleDragStart = (e, partId) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', partId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetPartId) => {
    e.preventDefault();
    const draggedPartId = e.dataTransfer.getData('text/plain');
    
    if (draggedPartId !== targetPartId && onReorderParts) {
      onReorderParts(draggedPartId, targetPartId);
    }
  };

  return (
    <div className={`relative bg-dark-card/80 backdrop-blur-xl border border-sunset-orange/30 rounded-2xl transition-all flex flex-col ${isCollapsed ? 'w-12' : 'w-full'} ${className}`}>
      {/* Collapse Toggle */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-4 z-10 bg-purple-600 hover:bg-purple-700 text-white rounded-full p-1 shadow-lg transition-transform hover:scale-110"
        title={isCollapsed ? 'Ouvrir' : 'Replier'}
      >
        {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>

      {isCollapsed ? (
        /* Collapsed View - Vertical Icons */
        <div className="flex flex-col items-center py-4 gap-3">
          <GripVertical className="w-5 h-5 text-purple-400" />
          <div className="text-xs text-sunset-orange writing-mode-vertical transform rotate-180">
            PARTIES
          </div>
        </div>
      ) : (
        /* Expanded View */
        <div className="p-4 flex flex-col h-full">
          <div className="flex items-center justify-between mb-4 flex-shrink-0">
            <h2 className="text-lg font-semibold text-purple-200 flex items-center gap-2">
              <GripVertical className="w-5 h-5" />
              Parties de la soirée
            </h2>
            {onCreatePart && (
              <Button
                onClick={onCreatePart}
                size="sm"
                variant="outline"
                className="border-sunset-orange/30 text-sunset-orange hover:bg-purple-500/10"
              >
                <Plus className="w-4 h-4 mr-1" />
                Partie
              </Button>
            )}
          </div>

      <div className="space-y-2 flex-1 overflow-y-auto" style={{scrollbarWidth: 'thin', scrollbarColor: 'rgb(147 51 234) rgb(30 41 59)'}}>
        {parts.length === 0 ? (
          <div className="text-center py-8 text-purple-300/70 text-sm">
            Aucune partie créée
          </div>
        ) : (
          parts.map((part) => {
            const isActive = part.id === currentPartId;
            
            return (
              <div
                key={part.id}
                draggable
                onDragStart={(e) => handleDragStart(e, part.id)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, part.id)}
                className={`
                  group relative p-3 rounded-lg cursor-move transition-all
                  ${isActive 
                    ? 'bg-gradient-to-r from-sunset-orange to-sunset-secondary text-white shadow-lg' 
                    : 'bg-dark-input/50 hover:bg-slate-700/50 text-gray-300'
                  }
                `}
              >
                {/* Drag Handle */}
                <div className="absolute left-1 top-1/2 -translate-y-1/2">
                  <GripVertical className={`w-4 h-4 ${isActive ? 'text-white/60' : 'text-purple-300/70'}`} />
                </div>

                {/* Part Info */}
                <div className="flex items-center justify-between pl-6">
                  <div 
                    className="flex-1 cursor-pointer"
                    onClick={() => onSelectPart && onSelectPart(part.id)}
                  >
                    <div className="font-semibold flex items-center gap-2">
                      {isActive && <Play className="w-4 h-4" fill="currentColor" />}
                      {part.name}
                    </div>
                    <div className={`text-xs ${isActive ? 'text-white/70' : 'text-purple-300/70'}`}>
                      {formatModeLabel(part.mode)}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-1">
                    {!isActive && onAddPartToQueue && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddPartToQueue(part.id);
                        }}
                        size="sm"
                        variant="ghost"
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Ajouter à la file d'attente"
                      >
                        <ListPlus className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

          {/* Instructions */}
          <div className="mt-4 pt-3 border-t border-purple-500/30">
            <p className="text-xs text-purple-300/80 leading-relaxed">
              💡 <span className="font-semibold">Glissez</span> pour réorganiser l'ordre des parties
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartsManager;
